import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useGetCompanyByIdQuery, useUpdateCompanyByIdMutation } from "../../../../../API/authApi";
import { IFinancesForm } from "../../../../../Interfaces/formValue-interfaces";
import { useDirectoriesHook, useToastify } from "../../../../../Hooks/hooks";
import useLanguagesHooks from "../../../../../Components/Languages/setLanguage-hooks";
import { IUpdateCompanyById } from "../../../../../Interfaces/request-interfaces";
import { CurrencyTypes, IAutocompleteItem } from "../../../../../Interfaces/componentTypes";
import { ICompanyResponse } from "../../../../../Interfaces/responseTypes";
import { getCompanyPosTerminal, parseCompanyPosTerminal, setCompanyPosTerminalStorage } from "../../../../../Utilits/utilits";

const buildSyncKey = (data: {
    id: number;
    cost_currency?: CurrencyTypes;
    service_fee?: string;
    pos_terminal?: boolean | null;
}) =>
    [
        data.id,
        data.cost_currency ?? '',
        data.service_fee ?? '',
        getCompanyPosTerminal(data),
    ].join('|');

const useFinancesHook = (companyId: number) => {
    const { data: businessData } = useGetCompanyByIdQuery({ id: companyId });
    const { showToastSuccess, showToastError } = useToastify();
    const [update, { isLoading, isError: isUpdateError }] = useUpdateCompanyByIdMutation();
    const { currencies } = useDirectoriesHook();
    const { t } = useLanguagesHooks();
    const {
        register,
        control,
        formState: { errors },
        setValue,
        getValues,
        watch
    } = useForm<IFinancesForm>({
        mode: 'all',
        defaultValues: { pos_terminal: false },
    });
    const [initialValues, setInitialValues] = useState<{
        service_fee?: string;
        cost_currency?: IAutocompleteItem;
        pos_terminal?: boolean | null;
    }>({
        service_fee: undefined,
        cost_currency: undefined,
        pos_terminal: undefined,
    });
    const lastSyncedKeyRef = useRef<string | null>(null);
    const prevCompanyIdRef = useRef(companyId);

    const initCostCurrency = watch('cost_currency');
    const initServiceFee = watch('service_fee');
    const initPosTerminal = watch('pos_terminal');

    const isCurrencyChanged =
        (initCostCurrency?.id ?? null) !== (initialValues.cost_currency?.id ?? null);
    const isServiceFeeChanged =
        String(initServiceFee ?? '') !== String(initialValues.service_fee ?? '');
    const isPosTerminalChanged =
        (initPosTerminal ?? false) !== (initialValues.pos_terminal ?? false);

    const isButtonActive = isCurrencyChanged || isServiceFeeChanged || isPosTerminalChanged;

    const applyServerData = (data: ICompanyResponse) => {
        const costCurrency = data.cost_currency
            ? currencies.find(item => item.id === data.cost_currency)
            : undefined;
        const posTerminal = data.pos_terminal === true || data.pos_terminal === false
            ? data.pos_terminal
            : getCompanyPosTerminal(data);

        setValue('service_fee', data.service_fee ?? '', { shouldDirty: false });
        if (costCurrency) {
            setValue('cost_currency', costCurrency, { shouldDirty: false });
        }
        setValue('pos_terminal', posTerminal ?? false, { shouldDirty: false });

        setInitialValues({
            service_fee: data.service_fee,
            cost_currency: costCurrency,
            pos_terminal: posTerminal ?? false,
        });

        lastSyncedKeyRef.current = buildSyncKey({
            ...data,
            pos_terminal: posTerminal ?? false,
        });
    };

    const syncFormFromServer = () => {
        if (!businessData) {
            return;
        }

        const syncKey = buildSyncKey(businessData);
        if (lastSyncedKeyRef.current === syncKey) {
            return;
        }

        const parsedPosTerminal = parseCompanyPosTerminal(businessData);
        if (parsedPosTerminal !== null) {
            setCompanyPosTerminalStorage(businessData.id, parsedPosTerminal);
        }

        applyServerData(businessData);
    };

    const updateFinance = async () => {
        const serviceFeeRaw = getValues('service_fee');
        const currencyId = getValues('cost_currency')?.id as CurrencyTypes;
        const posTerminal = Boolean(getValues('pos_terminal'));
        const payload: IUpdateCompanyById = {
            id: companyId,
            data: {
                cost_currency: currencyId,
                pos_terminal: posTerminal,
                ...(serviceFeeRaw !== undefined && serviceFeeRaw !== null && serviceFeeRaw !== ''
                    ? { service_fee: !isNaN(parseFloat(String(serviceFeeRaw))) ? parseFloat(String(serviceFeeRaw)).toFixed(2) : undefined }
                    : {}),
            } as IUpdateCompanyById['data'],
        };

        try {
            await update(payload).unwrap();
            setCompanyPosTerminalStorage(companyId, posTerminal);
            applyServerData({
                ...(businessData ?? { id: companyId } as ICompanyResponse),
                cost_currency: currencyId,
                service_fee: serviceFeeRaw
                    ? (!isNaN(parseFloat(String(serviceFeeRaw)))
                        ? parseFloat(String(serviceFeeRaw)).toFixed(2)
                        : businessData?.service_fee)
                    : businessData?.service_fee,
                pos_terminal: posTerminal,
            });
            showToastSuccess(t('Toast.Success.Common'));
        } catch {
            showToastError(t('Error.Common'));
        }
    };

    useEffect(() => {
        syncFormFromServer();
    }, [companyId, businessData?.id, businessData?.pos_terminal, businessData?.cost_currency, businessData?.service_fee]);

    useEffect(() => {
        if (prevCompanyIdRef.current !== companyId) {
            prevCompanyIdRef.current = companyId;
            lastSyncedKeyRef.current = null;
        }
    }, [companyId]);

    useEffect(() => {
        if (isUpdateError) {
            showToastError(t('Error.Common'));
        }
    }, [isUpdateError]);

    return {
        errors,
        control,
        isLoading,
        currencies,
        isButtonActive,
        register,
        updateFinance,
    }
};

export default useFinancesHook
